import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonConstants } from '../commons/common-constants';
import { Router } from '@angular/router';

import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest, EventMessage, EventType } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { LoggingServiceTsService } from '../store/loggingService/logging.service';
import { v4 as uuidv4 } from 'uuid';
import { ReferenceDataProvider } from '../commons/reference-data-provider';
import { AppInsightsService } from '../app-insights-service';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'hs-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy{

  loggedIn: boolean = false
  profile: any;
  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private logging: LoggingServiceTsService,
    public appInsightsService : AppInsightsService,
    private readonly referenceDataProvider : ReferenceDataProvider,
    private readonly toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.appInsightsService.trackPageEvent('Login Home Screen', 'Read','Home');
    this.checkAndSetActiveAccount();
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
      )
      .subscribe((result: EventMessage) => {
        try {
          const payload = result.payload as AuthenticationResult;
          this.authService.instance.setActiveAccount(payload.account);
          this.checkAndSetActiveAccount();
          this.getClaims(this.authService.instance.getActiveAccount()?.idTokenClaims);
        } catch (error) {
          console.error('[MSAL] Exception while processing login success event.', error);
        }
      });
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        try {
          this.checkAndSetActiveAccount();
          this.getClaims(this.authService.instance.getActiveAccount()?.idTokenClaims);
        } catch (error) {
          console.error('[MSAL] Exception while processing interaction status.', error);
        }
      })


  }
  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */


    try {
      const accounts = this.authService.instance.getAllAccounts();
      console.log('[MSAL] Account check completed.', { accountCount: accounts.length });

      if (accounts.length > 0) {
        this.loggedIn = true;
        this.authService.instance.setActiveAccount(accounts[0]);
        this.router.navigateByUrl('/dashboard');


        const navUrl = localStorage.getItem('requestedUrl');
        let url = CommonConstants.DEFAULT_HOME;
        try {
          url = navUrl && JSON.parse(navUrl)[0];
        } catch (error) {
          console.error('[MSAL] Exception while parsing requested URL.', error);
        }
        if (!url || url === '/' || url === '') {
          this.router.navigateByUrl(CommonConstants.DEFAULT_HOME);
        } else {
          this.router.navigateByUrl(url);
        }
        localStorage.removeItem('requestedUrl');
      }
    } catch (error) {
      console.error('[MSAL] Exception while checking the active account.', error);
    }
  }


  async loginRedirect() {
    this.appInsightsService.trackPageEvent('User Authentication : User Clicked On Sign In as Somatus Employee', 'Create','Home');
    const hasValidConfiguration = this.hasValidMsalConfiguration();
    console.log('[MSAL] Login redirect requested', {
      hasValidConfiguration,
      authClientIdConfigured: Boolean(environment.authClientID?.trim()) && !environment.authClientID.includes('_@_'),
      authorityConfigured: Boolean(environment.authAuthority?.trim()) && !environment.authAuthority.includes('_@_'),
      authorityUsesMicrosoftLogin: environment.authAuthority?.startsWith('https://login.microsoftonline.com/') ?? false,
      redirectUriConfigured: Boolean(environment.authRedirectURI?.trim()) && !environment.authRedirectURI.includes('_@_')
    });

    if (!hasValidConfiguration) {
      console.error('[MSAL] Login blocked because the deployed MSAL configuration is invalid.');
      this.toastr.error('Sign-in configuration is unavailable. Contact the application administrator.', 'Sign-in unavailable');
      return;
    }

    try {
      console.log('[MSAL] Starting redirect login.');
      if (this.msalGuardConfig.authRequest) {
        await this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
      } else {
        await this.authService.loginRedirect();
      }
    } catch (error) {
      console.error('[MSAL] Unable to start Microsoft sign-in.', error);
      this.toastr.error('Unable to start Microsoft sign-in. Please try again.', 'Sign-in failed');
    }
  }

  private hasValidMsalConfiguration(): boolean {
    const checks = {
      clientIdPresent: Boolean(environment.authClientID?.trim()),
      clientIdHasNoToken: !environment.authClientID?.includes('_@_'),
      authorityPresent: Boolean(environment.authAuthority?.trim()),
      authorityHasNoToken: !environment.authAuthority?.includes('_@_'),
      authorityUsesMicrosoftLogin: environment.authAuthority?.startsWith('https://login.microsoftonline.com/') ?? false,
      redirectUriPresent: Boolean(environment.authRedirectURI?.trim()),
      redirectUriHasNoToken: !environment.authRedirectURI?.includes('_@_')
    };
    const isValid = Object.values(checks).every(Boolean);
    console.log('[MSAL] Configuration validation result:', { isValid, checks });
    return isValid;
  }

  loginPopup() {
    if (this.msalGuardConfig.authRequest) {
      try {
        this.authService.loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
          }, (error) => {
            console.error('[MSAL] Popup login failed.', error);
            this.toastr.error('Unable to start Microsoft sign-in. Please try again.', 'Sign-in failed');
          });
      } catch (error) {
        console.error('[MSAL] Exception while starting popup login.', error);
        this.toastr.error('Unable to start Microsoft sign-in. Please try again.', 'Sign-in failed');
      }
    } else {
      try {
        this.authService.loginPopup()
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        }, (error) => {
          console.error('[MSAL] Popup login failed.', error);
          this.toastr.error('Unable to start Microsoft sign-in. Please try again.', 'Sign-in failed');
        });
      } catch (error) {
        console.error('[MSAL] Exception while starting popup login.', error);
        this.toastr.error('Unable to start Microsoft sign-in. Please try again.', 'Sign-in failed');
      }
    }
  }

  async logout(popup?: boolean) {
    if (popup) {
      await this.authService.logoutPopup({
        mainWindowRedirectUri: "/"
      });
    } 
    else {
      await this.authService.logoutRedirect();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
  async getProfile() {
    const graph_endpoint = await this.referenceDataProvider.getAzureConfigurationById('graph_endpoint') ?? '';
    this.http.get(graph_endpoint)
      .subscribe((profile: any) => {
        this.profile = profile;
      });
  }
  getClaims(claims: any) {
    const dataSource = [
      { id: 1, claim: "Display Name", value: claims ? claims['name'] : null },
      { id: 2, claim: "User Principal Name (UPN)", value: claims ? claims['preferred_username'] : null },
      { id: 2, claim: "OID", value: claims ? claims['oid'] : null }
    ];
    let sessionId = uuidv4();
    localStorage.setItem(CommonConstants.LS_SESSION,sessionId);
    localStorage.setItem(CommonConstants.LS_USERNAME, dataSource[1].value);
    localStorage.setItem(CommonConstants.LS_NAME, dataSource[0].value);
    if(claims != undefined){
      this.appInsightsService.trackPageEvent('User loggedIn Sucessfully', 'Read','Home');
    }

  }


}
